import type { FC, ReactNode } from 'react'
import type { Post } from '@/types'

import Link from 'next/link'
import { ArrowRight, Star } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { AuthorByline } from '@/components/blog/author-byline'

interface FeaturedPostProps {
    post: Post
    eyebrow?: string
}

export const FeaturedPost: FC<FeaturedPostProps> = ({ post, eyebrow = 'Featured' }): ReactNode => {
    const href = `/${post.hubSlug}/${post.slug}`

    return (
        <article className='group grid overflow-hidden rounded-xl border border-border bg-card md:grid-cols-2'>
            <Link
                href={href}
                className='order-1 block overflow-hidden bg-muted md:order-none'
                tabIndex={-1}
                aria-hidden='true'
            >
                <img
                    src={post.coverImage ?? '/images/hero-smile.jpg'}
                    alt=''
                    loading='eager'
                    decoding='async'
                    className='h-56 w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] md:h-full'
                />
            </Link>

            <div className='flex flex-col justify-center gap-4 p-6 sm:p-8 lg:p-10'>
                <div className='flex items-center gap-2'>
                    <Badge variant='sand'>
                        <Star className='size-3.5' />
                        {eyebrow}
                    </Badge>

                    <Badge variant='primary'>{post.hubName}</Badge>
                </div>

                <h2 className='font-heading text-2xl font-bold leading-tight tracking-tight text-foreground lg:text-3xl'>
                    <Link href={href} className='transition-colors hover:text-primary'>
                        {post.title}
                    </Link>
                </h2>

                <p className='line-clamp-3 text-[15px] leading-relaxed text-muted-foreground'>
                    {post.excerpt}
                </p>

                <AuthorByline
                    name={post.authorName}
                    role={post.authorRole}
                    date={post.publishedAt}
                    readMinutes={post.readMinutes}
                />

                <Link
                    href={href}
                    className='mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-primary'
                >
                    Read the guide
                    <ArrowRight className='size-4 transition-transform group-hover:translate-x-1' />
                </Link>
            </div>
        </article>
    )
}
